/**
 * Dashboard-level bulk refresh override (S12) — the UI prototype's
 * dashboard-level "every 30s" control, reinterpreted per the plan: picking a
 * cadence here sets `refresh.mode` on EVERY query-backed tile in the
 * dashboard, but only after an explicit confirm (each tile's individual
 * cadence is otherwise a per-tile setting, so this is a deliberate bulk
 * action, not a live dashboard-wide setting).
 *
 * The control also REPORTS the live state (UX review): when tiles are on an
 * interval it reads "Auto-refresh · 30s" and grows a hairline progress bar
 * counting down to the next cycle. Both come from the pure
 * `autoRefreshStatus` read model over the tiles' config + the runtime store's
 * last-run timestamps — `refreshScheduler` stays the only thing that schedules
 * anything; the 1s tick below only re-renders this label.
 */
import { useEffect, useMemo, useState } from 'react';
import { Timer } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isQueryBackedTile, type Dashboard } from '../types/dashboardTypes';
import { autoRefreshStatus, formatCadence } from '../services/autoRefreshStatus';
import { tileRuntimeKey, useDashboardRuntimeStore } from '../store/dashboardRuntimeStore';

const OPTIONS: Array<{ value: string; label: string; seconds: number | null }> = [
  { value: 'off', label: 'Manual only', seconds: null },
  { value: '30', label: 'Every 30s', seconds: 30 },
  { value: '60', label: 'Every 1m', seconds: 60 },
  { value: '300', label: 'Every 5m', seconds: 300 },
];

type BulkRefreshOverrideProps = {
  dashboard: Dashboard;
  onApply: (seconds: number | null) => void;
};

export function BulkRefreshOverride({ dashboard, onApply }: BulkRefreshOverrideProps) {
  const [pending, setPending] = useState<{ label: string; seconds: number | null } | null>(null);
  const queryTileCount = dashboard.tiles.filter(isQueryBackedTile).length;

  const runtimeByTile = useDashboardRuntimeStore((s) => s.byTile);
  const lastRunAtByTileId = useMemo(() => {
    const out: Record<string, number> = {};
    for (const tile of dashboard.tiles) {
      const at = runtimeByTile[tileRuntimeKey(dashboard.id, tile.id)]?.lastSuccess?.at;
      const ms = at ? Date.parse(at) : NaN;
      if (!Number.isNaN(ms)) out[tile.id] = ms;
    }
    return out;
  }, [runtimeByTile, dashboard.id, dashboard.tiles]);

  // Display-only tick: mounted ONLY while some tile is on an interval, and it
  // schedules nothing — it just re-reads the clock so the countdown moves.
  const [now, setNow] = useState(() => Date.now());
  const status = autoRefreshStatus(dashboard.tiles, lastRunAtByTileId, now);
  const autoOn = status.tileCount > 0;
  useEffect(() => {
    if (!autoOn) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [autoOn]);

  const bulkHint = `Pick a cadence to set all ${queryTileCount} query tile${queryTileCount === 1 ? '' : 's'} at once`;
  const title = autoOn
    ? `Auto-refresh: ${status.seconds === null ? 'mixed cadences' : `every ${formatCadence(status.seconds)}`} on ${status.tileCount} of ${queryTileCount} tiles${
        status.remainingSeconds === null ? '' : ` — next in ${status.remainingSeconds}s`
      }. ${bulkHint}.`
    : `${bulkHint}.`;

  return (
    <>
      <Select
        value=""
        onValueChange={(value) => {
          const option = OPTIONS.find((o) => o.value === value);
          if (option) setPending(option);
        }}
      >
        <SelectTrigger
          className="relative h-7 w-[168px] gap-1.5 overflow-hidden text-xs"
          title={title}
          data-testid="dashboard-bulk-refresh-trigger"
          data-auto-refresh={autoOn ? 'on' : 'off'}
        >
          <Timer
            className={`h-3.5 w-3.5 shrink-0 ${autoOn ? 'text-primary' : 'text-muted-foreground'}`}
            aria-hidden
          />
          <SelectValue placeholder={status.label} />
          {status.progress !== null && (
            // Hairline fill of the current cycle. Re-keyed per cycle so the
            // 1s transition never animates backwards across the reset.
            <span
              key={status.cycleStartedAt ?? 'cycle'}
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-left bg-primary/60 transition-transform duration-1000 ease-linear"
              // `display` inline: SelectTrigger's `[&>span]:line-clamp-1` (meant
              // for the value text) outranks a utility class on this child and
              // would swap its display box out from under the bar.
              style={{ display: 'block', transform: `scaleX(${status.progress})` }}
              data-testid="dashboard-bulk-refresh-progress"
              data-progress={status.progress.toFixed(2)}
            />
          )}
        </SelectTrigger>
        <SelectContent>
          {OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`dashboard-bulk-refresh-option-${o.value}`}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(next) => !next && setPending(null)}
        title={
          pending?.seconds != null
            ? `Set every tile to refresh ${pending.label.toLowerCase()}?`
            : 'Switch every tile to manual refresh?'
        }
        description={`This changes the refresh setting on all ${queryTileCount} query tile${queryTileCount === 1 ? '' : 's'} in this dashboard. Each tile keeps this setting until changed again.`}
        confirmLabel="Apply"
        onConfirm={() => {
          if (pending) onApply(pending.seconds);
          setPending(null);
        }}
      />
    </>
  );
}
